import { ComponentType } from 'react';

import { ContentProvider, initialValues as initialContentValues } from 'context/ContentContext';

import { CustomError } from 'ui/organisms/CustomError';

interface WithErrorHandlingProps {
  statusCode: number;
}

export const withErrorHandling = <P extends object>(
  WrappedComponent: ComponentType<P>,
): ComponentType<P & WithErrorHandlingProps> => {
  const WithErrorHandling = ({ statusCode, ...props }: WithErrorHandlingProps & P) => {
    if ([404, 500].includes(statusCode)) {
      // The error path bypasses <Page> (no ContentProvider), so wrap CustomError in one seeded with
      // the 'common' dictionary + locale that getServerSideProps attaches on error, letting useInt()
      // translate the 404/500 copy instead of falling back to English.
      const { internationalization, lang } = props as unknown as Partial<typeof initialContentValues>;

      return (
        <ContentProvider
          {...initialContentValues}
          internationalization={internationalization ?? initialContentValues.internationalization}
          lang={lang ?? initialContentValues.lang}
        >
          <CustomError statusCode={statusCode} />
        </ContentProvider>
      );
    }

    return <WrappedComponent {...(props as P)} />;
  };

  WithErrorHandling.displayName = `WithErrorHandling(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithErrorHandling;
};
